import { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, Select, Button, VStack, Spinner, Alert, AlertIcon, NumberInput,
  NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, useToast,
  RadioGroup, Radio, Stack
} from '@chakra-ui/react';
import { useAuth } from '../../Contexts/AuthContext';
import { useLang } from '../../Hooks';
import { getContracts, renewSubscription } from '../../Services/api';

const RenewModal = ({ isOpen, onClose }) => {
  const { texts, lang } = useLang();
  const { user } = useAuth();
  const toast = useToast();
  
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [months, setMonths] = useState(1);
  const [renewalType, setRenewalType] = useState('Renewal');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && user?.CustomerId) {
      setIsLoading(true);
      setError('');
      getContracts(user.CustomerId)
        .then(response => {
          setContracts(response.data?.data?.results || []);
        })
        .catch(err => {
          setError(err.message || 'Failed to fetch contracts.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (!isOpen) {
      setSelectedContract('');
      setMonths(1);
      setRenewalType('Renewal');
      setError('');
    }
  }, [isOpen, user]);

  const handleRenew = async () => {
    setIsSubmitting(true);
    const renewalData = {
      ContractId: parseInt(selectedContract),
      Months: months,
      Type: renewalType
    };

    try {
      const response = await renewSubscription(renewalData);
      if (response.data && !response.data.error) {
        toast({
          title: texts.renew?.successTitle || "Success",
          description: response.data.message || texts.renew?.successMessage || "Your request has been submitted.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: 'top'
        });
        onClose();
      } else {
        throw new Error(response.data?.message || 'Request failed.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An unexpected error occurred.';
      toast({
        title: texts.renew?.errorTitle || "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled = !selectedContract || months < 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={(e) => { e.preventDefault(); handleRenew(); }} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <ModalHeader>{texts.renew?.title || 'Renew / Upgrade Subscription'}</ModalHeader>
        <ModalCloseButton position="absolute" left={lang === 'ar' ? "8px" : "unset"} right={lang === 'ar' ? "unset" : "8px"} />
        <ModalBody>
          {isLoading ? (
            <VStack justify="center" h="200px"><Spinner /></VStack>
          ) : error ? (
            <Alert status="error"><AlertIcon />{error}</Alert>
          ) : (
            <VStack spacing={5}>
              <FormControl isRequired>
              <FormLabel>{texts.renew?.selectContract || 'Select Contract'}</FormLabel>
              <Select
                value={selectedContract}
                onChange={(e) => setSelectedContract(e.target.value)}
                placeholder={texts.renew?.selectPlaceholder || 'Choose a contract'}
                textAlign={lang === 'ar' ? 'right' : 'left'}
                sx={{
                  '.chakra-select__icon-wrapper': {
                    left: lang === 'ar' ? '0.5rem' : 'auto',
                    right: lang === 'ar' ? 'auto' : '0.5rem',
                  }
                }}
              >
                {contracts.map(contract => (
                  <option key={contract.ContractId} value={contract.ContractId}>
                    {contract.ContractTaskName}
                  </option>
                ))}
              </Select>
            </FormControl>

              <FormControl isRequired>
                <FormLabel>{texts.renew?.months || 'Number of Months'}</FormLabel>
                <NumberInput 
                  min={1} 
                  value={months} 
                  onChange={(valueString) => setMonths(parseInt(valueString) || 1)}
                  position="relative"
                >
                  <NumberInputField 
                    textAlign={lang === 'ar' ? 'right' : 'left'}
                    paddingLeft={lang === 'ar' ? '2.5rem' : '0.75rem'}
                    paddingRight={lang === 'ar' ? '0.75rem' : '2.5rem'}
                  />
                  <NumberInputStepper 
                    position="absolute"
                    left={lang === 'ar' ? '0' : 'auto'}
                    right={lang === 'ar' ? 'auto' : '0'}
                  >
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl as="fieldset" isRequired>
                <FormLabel as="legend">{texts.renew?.type || 'Action Type'}</FormLabel>
                <RadioGroup onChange={setRenewalType} value={renewalType}>
                  <Stack direction="row" spacing={5}>
                    <Radio value="Renewal">{texts.renew?.renewalOption || 'Renewal'}</Radio>
                    <Radio value="Upgrade">{texts.renew?.upgradeOption || 'Upgrade'}</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" me={3} onClick={onClose}>
            {texts.CANCEL || 'Cancel'}
          </Button>
          <Button
            colorScheme="blue"
            type="submit"
            isDisabled={isButtonDisabled || isLoading || isSubmitting}
            isLoading={isSubmitting}
            loadingText={texts.SAVING || "Saving..."}
          >
            {texts.renew?.button || 'Submit'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RenewModal;